// ABOUTME: Chat window component for service desk ticket conversations.
// ABOUTME: Handles message grouping, rendering, and auto-scroll to bottom.

import { Box, useTheme } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import ChatMessageClient from "./ChatMessageClient";
import ChatMessageFulfilmentcenter from "./ChatMessageFulfilmentcenter";
import ChatInput from "./ChatInput";
import { support_ticket_activity_action } from "../../../../../../utils/staticEnums";
import { tokens } from "../../../../../../theme";
import ChatGroupItem from "./ChatGroupItem";
import { isMessageHiddenFor,groupMessages } from "./messageGroupingSettings";



const ChatWindow = (props) => {
    const { t } = useTranslation();
    const chatScrollRef = useRef(null);
    const inputAreaRef = useRef(null);
    const spacerRef = useRef(null);

    const viewer = props.viewer || 'fulfilmentcenter';
    const groups = useMemo(() => {
        const visible = (props.messages || []).filter((msg) => !isMessageHiddenFor(msg, viewer));
        return groupMessages(visible);
    }, [props.messages, viewer]);

    useEffect(() => {
        if (chatScrollRef.current) {
            chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
        }
    }, [groups]);

    // Dynamically update spacer height based on input area height
    useEffect(() => {
        const inputArea = inputAreaRef.current;
        const spacer = spacerRef.current;
        if (!inputArea || !spacer) return;

        const updateSpacerHeight = () => {
            // Input bottom offset (32) + actual input height + buffer (8)
            const height = 32 + inputArea.offsetHeight + 8;
            spacer.style.height = `${height}px`;
        };

        updateSpacerHeight();

        const resizeObserver = new ResizeObserver(updateSpacerHeight);
        resizeObserver.observe(inputArea);

        return () => resizeObserver.disconnect();
    }, []);

    return (
        <Box
            sx={{
                mt: 3,
                flex: 1,
                position: 'relative',
                display: "flex",
                flexDirection: 'column',
                gap: 3,
                overflow: "hidden",
            }}
        >
            {/* Scrollable chat container */}
            <Box
                ref={chatScrollRef}
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    overflow: "auto",
                    overscrollBehavior: "contain",
                }}
            >
                {groups.map((group, groupIndex) => (
                    <ChatGroupItem
                        key={groupIndex} 
                        group={group} 
                        viewer={viewer} 
                    />
                ))}

                <Box
                    ref={spacerRef}
                    sx={{
                        flexShrink: 0,
                    }}
                />
            </Box>

            {/* Input area (or read-only footer for closed tickets). Hidden
                entirely when read-only AND no footer is provided. */}
            {(!props.readOnly || props.readOnlyFooter) && (
                <Box
                    ref={inputAreaRef}
                    sx={{
                        minHeight: props.readOnly ? 0 : 120,
                        maxHeight: 280,
                        width: "calc(100% - 64px)",
                        bottom: 32,
                        left: 32,
                        right: 32,
                        position: 'absolute',
                        borderRadius: 6,
                        background: props.theme.palette.mode === "dark" ? props.colors.grey[300] : props.colors.grey[0],
                        boxShadow: `0px 4px 50px ${props.theme.palette.mode === "dark" ? props.colors.grey[300] : props.colors.grey[200]}`,
                        p: 2,
                        border: `1px solid ${props.theme.palette.mode === "dark" ? props.colors.grey[500] : props.colors.grey[200]}`,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    }}
                >
                    {props.readOnly
                        ? props.readOnlyFooter
                        : <ChatInput onSend={props.onSend} allowResolveOnSend={props.allowResolveOnSend} colors={props.colors} theme={props.theme} viewer={viewer} />}
                </Box>
            )}
        </Box>
    );
};

export default ChatWindow;
